import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { NOT_APPLICABLE_SPACE_ID } from 'app/components/commons/Experience/PracticalInfo/SpacesSelect/SpacesSelect';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { CORE_QUERY_KEYS } from 'app/hooks/data/core/admin/experiences';
import {
  mapExperienceToCreateRequest,
  mapExperienceToUpdateRequest,
} from 'app/hooks/data/core/admin/experiences/mapping/form';
import { useCreateExperience } from 'app/hooks/data/core/admin/experiences/useCreateExperience';
import { useDeleteExperience } from 'app/hooks/data/core/admin/experiences/useDeleteExperience';
import { useUpdateExperience } from 'app/hooks/data/core/admin/experiences/useUpdateExperience';
import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import type { ExperienceDurationDescription } from 'app/typings/core/common/experiences/enum/experienceDurationDescription';
import { dayjs } from 'app/utils/dayjs';
import { pluralize } from 'app/utils/strings';

import {
  activeTabHasFieldError,
  getRejectedFields,
} from '../experienceTabFields';
import { MenuItemName } from '../menuItem';

import type { ExperienceInitialFormType } from './useExperienceData';
import {
  resetFormDirty,
  resetValidationErrors,
  setStockValidationError,
  setValidationErrors,
  validateAllConfigurations,
} from './useValidationStore';

const cleanExperienceValues = <T extends Partial<ExperienceInitialFormType>>(
  values: T
) => ({
  ...values,
  closedDaysBeforeCheckIn: values.lastMinuteRule
    ? values.closedDaysBeforeCheckIn
    : null,
  maxQuantity: values.maxAddonQuantity ? values.maxQuantity : null,
  spaceIds:
    values.spaceIds?.filter((id) => id !== NOT_APPLICABLE_SPACE_ID) || [],
  time: values.time ? dayjs(values.time).format('HH:mm') : undefined,
});

type UseExperienceActionsProps = {
  experienceId?: number;
  isCreatePage: boolean;
  initialValues?: Partial<ExperienceInitialFormType>;
  activeTab: MenuItemName;
};

export const useExperienceActions = ({
  experienceId,
  isCreatePage,
  initialValues,
  activeTab,
}: UseExperienceActionsProps) => {
  const [form] = Form.useForm<ExperienceInitialFormType>();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  const { mutateAsync: createExperience } = useCreateExperience();
  const { mutateAsync: updateExperience } = useUpdateExperience();
  const { mutateAsync: deleteExperience } = useDeleteExperience();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDurationModal, setShowDurationModal] = useState<boolean>(false);

  useEffect(() => {
    if (isCreatePage) {
      enterEditMode();
    }
  }, [isCreatePage, enterEditMode]);

  const formatDuration = (
    duration?: number,
    durationDescription?: ExperienceDurationDescription
  ): string | null => {
    if (!duration || !durationDescription) {
      return null;
    }

    const durationMap: Record<ExperienceDurationDescription, string> = {
      MINUTE: pluralize(duration, 'min', 'mins'),
      HOUR: pluralize(duration, 'hour', 'hours'),
      HALF_DAY: 'half day',
      DAY: pluralize(duration, 'day', 'days'),
    };

    return durationMap[durationDescription];
  };

  const formatName = useCallback(
    (experience: ExperienceInitialFormType, categoryName?: string): string => {
      const parts: string[] = [];

      if (categoryName) {
        parts.push(categoryName);
      }

      if (experience.location) {
        parts.push(experience.location);
      }

      const duration = formatDuration(
        experience.duration,
        experience.durationDescription
      );

      if (duration) {
        parts.push(duration);
      }

      return parts.join(' • ');
    },
    []
  );

  const onFinish = useCallback(
    async (values: ExperienceInitialFormType) => {
      const allValues = {
        ...form.getFieldsValue(true),
        ...values,
      } as ExperienceInitialFormType;

      const cleanedValues = cleanExperienceValues(allValues);

      try {
        if (isCreatePage) {
          const createdExperience = await createExperience(
            mapExperienceToCreateRequest(cleanedValues)
          );

          const location = {
            pathname: `/activities/${createdExperience?.id}`,
          };

          history.push(location);
        } else if (experienceId) {
          const payload = mapExperienceToUpdateRequest(
            cleanedValues,
            initialValues && cleanExperienceValues(initialValues)
          );

          if (Object.keys(payload).length === 0) {
            enterViewMode();

            return;
          }

          await updateExperience({
            id: experienceId,
            payload,
          });

          queryClient.invalidateQueries([
            CORE_QUERY_KEYS.experience,
            experienceId,
          ]);

          enterViewMode();
        } else {
          return;
        }

        resetFormDirty();
      } finally {
        setIsLoading(false);
      }
    },
    [
      createExperience,
      enterViewMode,
      experienceId,
      form,
      history,
      initialValues,
      isCreatePage,
      queryClient,
      updateExperience,
    ]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    resetValidationErrors();
    resetFormDirty();
    enterViewMode();
  }, [form, enterViewMode]);

  const handleDelete = useCallback(async () => {
    if (experienceId) {
      await deleteExperience(experienceId);
      history.push('/activities');
    }
  }, [deleteExperience, experienceId, history]);

  const submitForm = useCallback(() => {
    setStockValidationError({ isValid: true, errors: [] });
    setIsLoading(true);

    void onFinish(form.getFieldsValue(true)).catch(() => setIsLoading(false));
  }, [form, onFinish]);

  const hasDurationChanged = useCallback(() => {
    if (!initialValues) {
      return false;
    }

    const values = form.getFieldsValue(true);

    return (
      values?.duration !== initialValues.duration ||
      values?.durationDescription !== initialValues.durationDescription
    );
  }, [form, initialValues]);

  const hadActiveTimeSlots = useCallback(
    () => initialValues?.availabilityMode === AVAILABILITY_MODE.TIMESLOT,
    [initialValues]
  );

  const handleSave = useCallback(async () => {
    try {
      await form.validateFields();
    } catch (error) {
      const rejectedFields = getRejectedFields(error);

      const blocks = isCreatePage
        ? rejectedFields.length > 0
        : activeTabHasFieldError(rejectedFields, activeTab);

      if (blocks) {
        setCanSubmit(false);

        return;
      }
    }

    if (activeTab === MenuItemName.Availability || isCreatePage) {
      const { stockError, priceError } = validateAllConfigurations(
        form.getFieldsValue(true)
      );

      if (!stockError.isValid || !priceError.isValid) {
        setValidationErrors({ stockError, priceError });
        setCanSubmit(false);

        return;
      }
    }

    if (!isCreatePage && hasDurationChanged() && hadActiveTimeSlots()) {
      setShowDurationModal(true);

      return;
    }

    submitForm();
  }, [
    form,
    activeTab,
    isCreatePage,
    hasDurationChanged,
    hadActiveTimeSlots,
    submitForm,
  ]);

  const handleDurationModalConfirm = useCallback(() => {
    setShowDurationModal(false);
    submitForm();
  }, [submitForm]);

  const handleDurationModalCancel = useCallback(() => {
    setShowDurationModal(false);
  }, []);

  return {
    form,
    mode,
    canSubmit,
    isLoading,
    onFinish,
    onCancel,
    handleDelete,
    handleSave,
    enterEditMode,
    enterViewMode,
    setCanSubmit,
    formatName,
    showDurationModal,
    handleDurationModalConfirm,
    handleDurationModalCancel,
  };
};
